'use client';

import { useCallback, useRef, useState } from 'react';
import { useToast } from '@/shared/components/providers/ToastProvider';
import { useAppDispatch } from '@/store/hooks';
import { fetchAndamentosArbitral } from '../store/actions';
import type { ExportarHistoricoResultado } from '../types/arbitralDetailTypes';
import { arbitralDetalhesService } from '../store/services';

// Espelha o teto de `useBaixarDocumento`: a planilha é gerada sob demanda no servidor, então a
// requisição pode pendurar bem além de um GET comum. Sem teto, o `finally` nunca roda e o botão
// fica em "carregando" para sempre — só recarregando a página.
const TETO_TRANSFERENCIA_EXPORTACAO_MS = 120_000;

/**
 * "Exportar histórico": o arquivo vem pronto do core (`exceljs`), o front só decide se há o que
 * exportar, dispara o download e avisa o erro.
 */
export function useExportarHistorico(processoId: string, numeroProcesso: string): ExportarHistoricoResultado {
  const dispatch = useAppDispatch();
  const toast = useToast();
  const [exportando, setExportando] = useState(false);
  // Trava SÍNCRONA. `exportando` só vale no render seguinte, e o primeiro `await` é o fetch dos
  // andamentos: dois cliques nessa janela passariam pelo estado e gerariam dois XLSX.
  const emCurso = useRef(false);
  const exportar = useCallback(async () => {
    if (emCurso.current) return;
    emCurso.current = true;
    setExportando(true);
    try {
      // Busca os andamentos frescos (na aba Resumo o detalhe pode não tê-los embutidos) só para
      // saber se há o que exportar — o conteúdo do arquivo vem do core.
      const resultado = await dispatch(fetchAndamentosArbitral(processoId));
      if (!fetchAndamentosArbitral.fulfilled.match(resultado)) {
        toast.showError('Erro ao exportar histórico', 'Não foi possível ler os andamentos.');
        return;
      }
      if (resultado.payload.length === 0) {
        toast.showError('Nada para exportar', 'Este processo ainda não tem andamentos.');
        return;
      }

      // O teto nasce aqui, não no início: a busca acima não recebe o `signal`, então contá-la
      // roubaria do orçamento do download — busca lenta faria o download começar já abortado.
      const controlador = new AbortController();
      const temporizador = setTimeout(() => controlador.abort(), TETO_TRANSFERENCIA_EXPORTACAO_MS);
      try {
        await arbitralDetalhesService.exportarHistoricoAndamentos(processoId, numeroProcesso, controlador.signal);
      } finally {
        clearTimeout(temporizador);
      }
    } catch {
      toast.showError('Erro ao exportar histórico', 'Tente novamente.');
    } finally {
      emCurso.current = false;
      setExportando(false);
    }
  }, [dispatch, numeroProcesso, processoId, toast]);
  return {
    exportar,
    exportando
  };
}